import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { publicApi, currency, categoryMeta } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/lib/content";
import { Minus, Plus, ArrowLeft, ShoppingBag } from "lucide-react";

function Stepper({ label, value, setValue, min = 1, testid }) {
  return (
    <div>
      <p className="overline-label mb-2">{label}</p>
      <div className="flex items-center gap-4">
        <button data-testid={`${testid}-decrement`} onClick={() => setValue(Math.max(min, value - 1))}
          className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold hover:text-ink transition-colors">
          <Minus className="w-4 h-4" />
        </button>
        <span data-testid={`${testid}-value`} className="w-10 text-center text-xl font-serif text-cream">{value}</span>
        <button data-testid={`${testid}-increment`} onClick={() => setValue(value + 1)}
          className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold hover:text-ink transition-colors">
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { addItem } = useCart();
  const { t } = useContent();
  const [qty, setQty] = useState(1);
  const [days, setDays] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => (await publicApi.get(`/products/${id}`)).data,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 py-20 text-muted2">Loading…</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-muted2">Product not found.</div>;

  const handleSignIn = () => { localStorage.setItem("post_login_redirect", `/product/${id}`); login(); };
  const addToCart = () => {
    addItem(product, qty, days);
    toast.success("Added to your reservation quote");
    navigate("/cart");
  };

  const imgs = product.image_urls?.length ? product.image_urls : [""];
  const caps = product.image_captions || [];
  const cat = categoryMeta[product.category]?.label || product.category;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/catalog" className="inline-flex items-center gap-2 text-muted2 hover:text-gold mb-8 text-sm">
        <ArrowLeft className="w-4 h-4" /> {t("product.back")}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="rounded-2xl overflow-hidden border border-gold/25">
            <img src={imgs[activeImg]} alt={caps[activeImg] || product.name} className="w-full h-full object-cover aspect-square" />
          </div>
          {caps[activeImg] && <p data-testid="product-gallery-caption" className="mt-3 text-sm text-muted2 italic text-center">{caps[activeImg]}</p>}
          {imgs.length > 1 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {imgs.map((u, i) => (
                <button key={i} data-testid={`product-gallery-thumb-${i}`} onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border transition-colors ${i === activeImg ? "border-gold" : "border-gold/20 hover:border-gold/50"}`}>
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="overline-label mb-3">{cat}</p>
          <h1 data-testid="product-detail-name" className="font-serif text-4xl lg:text-5xl text-cream leading-tight">{product.name}</h1>
          <p className="mt-4 text-3xl text-gold-bright font-semibold">{currency(product.price_per_day)}<span className="text-muted2 text-base font-normal"> / day</span></p>
          <p className="mt-6 text-muted2 leading-relaxed">{product.description}</p>
          <p className="mt-4 text-sm text-sterling">{product.stock} {t("product.stock_suffix")}</p>

          <div className="mt-10 flex flex-wrap gap-10">
            <Stepper label={t("product.quantity")} value={qty} setValue={setQty} testid="product-quantity" />
            <Stepper label={t("product.days")} value={days} setValue={setDays} testid="product-days" />
          </div>

          <div className="mt-8 p-5 bg-ink-700 border border-gold/20 rounded-xl flex items-center justify-between">
            <span className="text-muted2 text-sm">{t("product.subtotal")}</span>
            <span data-testid="product-subtotal" className="text-xl font-serif text-gold">{currency(product.price_per_day * qty * days)}</span>
          </div>

          <div className="mt-8">
            {user ? (
              <button data-testid="product-add-to-cart-button" onClick={addToCart} className="btn-gold w-full sm:w-auto">
                <ShoppingBag className="w-4 h-4" /> {t("product.add_to_cart")}
              </button>
            ) : (
              <button data-testid="product-signin-to-reserve-button" onClick={handleSignIn} className="btn-gold w-full sm:w-auto">
                {t("product.signin")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
